import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useFunnel } from '../../contexts/FunnelContext'
import { PrimaryButton } from '../../components/funnel/PrimaryButton'
import { LoadingSpinner } from '../../components/funnel/LoadingSpinner'
import { Shield, Phone, ArrowLeft, Sparkles, Check } from 'lucide-react'
import { getSupabaseClient } from '../../lib/supabaseClient'
import { notifyLeadByEmail } from '../../lib/notifyLead'

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate()
  const { state, setFullName, setPhone, setAddress, setPreferredServiceWindow } = useFunnel()
  const [formData, setFormData] = useState({
    fullName: state.fullName || '',
    phone: state.phone || '',
    address: state.address || '',
    preferredServiceWindow: state.preferredServiceWindow || '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const packagePrice = state.selectedPackage === 'refresh' ? 199 : 349
  const discount = 100
  const totalDue = packagePrice - discount

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (field === 'fullName') setFullName(value)
    if (field === 'phone') setPhone(value)
    if (field === 'address') setAddress(value)
    if (field === 'preferredServiceWindow') setPreferredServiceWindow(value)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const supabase = getSupabaseClient()
      await supabase.from('leads').insert({
        source: 'checkout',
        full_name: formData.fullName,
        email: state.email || null,
        phone: formData.phone,
        payload: {
          address: formData.address,
          preferredServiceWindow: formData.preferredServiceWindow,
          selectedPackage: state.selectedPackage,
        },
      })
      notifyLeadByEmail({
        source: 'checkout',
        full_name: formData.fullName,
        email: state.email ?? undefined,
        phone: formData.phone,
        payload: { address: formData.address, preferredServiceWindow: formData.preferredServiceWindow, selectedPackage: state.selectedPackage },
      })
    } catch (_) {
      // Continue to next page even if lead save fails (e.g. no Supabase env)
    }
    // Payment: lead captured above; team confirms appointment and arranges payment (card/invoice)—not charged in-browser today.
    await new Promise((resolve) => setTimeout(resolve, 1500))
    navigate('/refresh-confirmed-offer')
  }

  const isFormValid =
    formData.fullName && formData.phone && formData.address && formData.preferredServiceWindow

  return (
    <div style={{ backgroundColor: 'rgb(246, 246, 241)', minHeight: '100vh', padding: '2rem 0' }}>
      <div className="mx-auto max-w-4xl px-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Left Column - Order Summary & Info */}
          <div>
            {/* Order Summary */}
            <div className="bg-white p-6 rounded-xl border-2 mb-6" style={{ borderColor: '#dee2e6' }}>
              <h2 className="text-2xl font-semibold mb-4" style={{ color: 'rgb(15,15,15)' }}>
                Your Order: Hudson Home {state.selectedPackage === 'deluxe' ? 'Deluxe' : ''}{' '}
                Refresh
              </h2>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span style={{ color: 'rgb(76,76,75)' }}>Package Price</span>
                  <span style={{ color: 'rgb(15,15,15)' }}>${packagePrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'rgb(76,76,75)' }}>Limited-Time Offer</span>
                  <span style={{ color: '#145F6B' }}>-${discount.toFixed(2)}</span>
                </div>
                <div className="border-t-2 pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-semibold" style={{ color: 'rgb(15,15,15)' }}>
                      Total Due Today
                    </span>
                    <span className="text-2xl font-bold" style={{ color: '#AB9381' }}>
                      ${totalDue.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-sm" style={{ color: 'rgb(76,76,75)' }}>
                Your $100 gift card will be delivered via email after your service is complete.
              </p>
            </div>

            {/* Value Proposition Recap */}
            <div className="bg-white p-6 rounded-xl mb-6">
              <h3 className="text-xl font-semibold mb-3" style={{ color: 'rgb(15,15,15)' }}>
                Protect Your Home, Reclaim Your Time.
              </h3>
              <p className="text-base" style={{ color: 'rgb(76,76,75)' }}>
                You're just a few steps away from a safer, more efficient home this winter. Our
                expert technicians will handle everything.
              </p>
            </div>

            {/* Testimonial */}
            <div className="bg-white p-6 rounded-xl mb-6 border-l-4" style={{ borderColor: '#145F6B' }}>
              <blockquote className="text-base italic mb-2" style={{ color: 'rgb(76,76,75)' }}>
                "The peace of mind is invaluable. Hudson found an issue we never would have caught,
                saving us a fortune. Highly recommend!"
              </blockquote>
              <cite className="text-sm" style={{ color: 'rgb(76,76,75)' }}>
                - Sarah L., West Des Moines
              </cite>
            </div>

            {/* Guarantee */}
            <div className="bg-white p-6 rounded-xl mb-6">
              <div className="flex items-start gap-3 mb-3">
                <Shield className="w-6 h-6 flex-shrink-0" style={{ color: '#145F6B' }} />
                <h3 className="text-xl font-semibold" style={{ color: 'rgb(15,15,15)' }}>
                  Our Hudson Standard Guarantee
                </h3>
              </div>
              <p className="text-base" style={{ color: 'rgb(76,76,75)' }}>
                We stand by our work. If you are not 100% satisfied with your Home Refresh service,
                we will make it right.
              </p>
            </div>

            {/* Enhanced Membership Upsell */}
            <div className="bg-white p-6 rounded-xl border-2 shadow-md" style={{ borderColor: '#145F6B' }}>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5" style={{ color: '#AB9381' }} />
                <h3 className="text-xl font-semibold" style={{ color: 'rgb(15,15,15)' }}>
                  Upgrade to Full Membership & Save
                </h3>
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#145F6B' }} />
                  <p className="text-sm" style={{ color: 'rgb(76,76,75)' }}>
                    <strong>Apply your $50-$100 service credit</strong> toward your first month of
                    membership
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#145F6B' }} />
                  <p className="text-sm" style={{ color: 'rgb(76,76,75)' }}>
                    <strong>Ongoing monthly maintenance</strong> instead of one-time service
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#145F6B' }} />
                  <p className="text-sm" style={{ color: 'rgb(76,76,75)' }}>
                    <strong>24/7 concierge access</strong> for all your home needs
                  </p>
                </div>
              </div>
              <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: 'rgb(246, 246, 241)' }}>
                <p className="text-xs font-semibold mb-1" style={{ color: '#145F6B' }}>
                  MEMBERSHIP STARTS AT $399/MONTH
                </p>
                <p className="text-xs" style={{ color: 'rgb(76,76,75)' }}>
                  Your service credit reduces your first month to as low as $299
                </p>
              </div>
              <Link
                to="/services"
                className="inline-block w-full text-center px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                style={{ backgroundColor: '#145F6B', color: 'white' }}
              >
                Learn About Membership →
              </Link>
            </div>
          </div>

          {/* Right Column - Checkout Form */}
          <div>
            <div className="bg-white p-6 rounded-xl border-2" style={{ borderColor: '#dee2e6' }}>
              <h2 className="text-2xl font-semibold mb-6" style={{ color: 'rgb(15,15,15)' }}>
                Complete Your Booking
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'rgb(15,15,15)' }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    required
                    className="w-full px-4 py-3 border-2 rounded-lg"
                    style={{ borderColor: '#dee2e6' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'rgb(15,15,15)' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={state.email}
                    disabled
                    className="w-full px-4 py-3 border-2 rounded-lg bg-gray-100"
                    style={{ borderColor: '#dee2e6' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'rgb(15,15,15)' }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                    className="w-full px-4 py-3 border-2 rounded-lg"
                    style={{ borderColor: '#dee2e6' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'rgb(15,15,15)' }}>
                    Home Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    required
                    rows={3}
                    className="w-full px-4 py-3 border-2 rounded-lg"
                    style={{ borderColor: '#dee2e6' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'rgb(15,15,15)' }}>
                    Preferred Service Window
                  </label>
                  <select
                    value={formData.preferredServiceWindow}
                    onChange={(e) => handleInputChange('preferredServiceWindow', e.target.value)}
                    required
                    className="w-full px-4 py-3 border-2 rounded-lg"
                    style={{ borderColor: '#dee2e6' }}
                  >
                    <option value="">Select a time window</option>
                    <option value="weekday-morning">Weekday Mornings</option>
                    <option value="weekday-afternoon">Weekday Afternoons</option>
                    <option value="weekend">Weekends</option>
                    <option value="anytime">Anytime</option>
                  </select>
                  <p className="text-sm mt-2" style={{ color: 'rgb(76,76,75)' }}>
                    We will call you within 24 hours to confirm your exact appointment time.
                  </p>
                </div>

                <div className="mt-6 p-4 border-2 rounded-lg" style={{ borderColor: '#dee2e6', backgroundColor: 'rgb(246, 246, 241)' }}>
                  <p className="text-sm mb-2 font-semibold" style={{ color: 'rgb(15,15,15)' }}>
                    Payment
                  </p>
                  <p className="text-xs" style={{ color: 'rgb(76,76,75)' }}>
                    We&apos;ll reach out within 24 hours to confirm your visit and finalize payment. No card required on this step.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className="w-full px-10 py-4 rounded-full font-bold text-lg transition-colors duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                  style={{ backgroundColor: '#AB9381', color: 'white' }}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <LoadingSpinner size="sm" />
                      <span>Processing Your Order...</span>
                    </div>
                  ) : (
                    'Book My Home Refresh Now'
                  )}
                </button>

                {/* Back Button */}
                <button
                  type="button"
                  onClick={() => navigate('/winter-refresh/step-4')}
                  className="w-full mt-3 flex items-center justify-center gap-2 text-sm font-medium"
                  style={{ color: 'rgb(76,76,75)' }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Package Selection
                </button>
              </form>
            </div>

            {/* Downsell Section */}
            <div className="mt-6 bg-white p-6 rounded-xl border-2" style={{ borderColor: '#dee2e6' }}>
              <div className="flex items-start gap-4">
                <Phone className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: '#145F6B' }} />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2" style={{ color: 'rgb(15,15,15)' }}>
                    Need More Time to Decide?
                  </h3>
                  <p className="text-sm mb-3" style={{ color: 'rgb(76,76,75)' }}>
                    Schedule a free consultation call with our Home Director. We'll assess your
                    home's needs and answer any questions—no pressure, just expert guidance.
                  </p>
              <Link
                to="/services"
                className="inline-block text-sm font-semibold"
                style={{ color: '#145F6B' }}
              >
                Schedule Free Consultation →
              </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CheckoutPage

